import { useState, useEffect } from "react";
import { useUserOrgPreferences } from "../../org/hooks";
import { OrgKey } from "../../generated/core-api";
import { Button, Checkbox, H4 } from "@datapay-by-datacom/design-system";
import s from "./UserOrgPreferencesForm.module.css";

export function UserOrgPreferencesForm() {
  const { preferences, userOrgs, isLoading, setPreferences } =
    useUserOrgPreferences();
  const [formState, setFormState] = useState<OrgKey[]>([]);

  // Initialize form state when preferences load
  useEffect(() => {
    if (preferences) {
      setFormState(preferences);
    }
  }, [preferences]);

  const handleOrgToggle = (
    orgId: string,
    orgCategory: OrgKey["category"],
    isChecked: boolean
  ) => {
    if (isChecked) {
      // Add org to preferences if not already included
      const orgKey: OrgKey = { id: orgId, category: orgCategory };
      setFormState((prev) =>
        prev.some((p) => p.id === orgId) ? prev : [...prev, orgKey]
      );
    } else {
      // Remove org from preferences
      setFormState((prev) => prev.filter((p) => p.id !== orgId));
    }
  };

  const handleSubmit = () => {
    setPreferences.mutate(formState);
  };

  const handleReset = () => {
    setFormState(preferences || []);
  };

  if (isLoading) {
    return <div>Loading your organizations...</div>;
  }

  const hasChanges =
    JSON.stringify(formState.sort((a, b) => a.id.localeCompare(b.id))) !==
    JSON.stringify(preferences?.sort((a, b) => a.id.localeCompare(b.id)));

  return (
    <div className={s.container}>
      <H4>Default Knowledge Sources</H4>
      <p className={s.description}>
        Select which organizations should be included by default when creating
        new threads. New threads will have access to knowledge from these
        organizations automatically, though you can always adjust this for
        individual threads.
      </p>

      {userOrgs.length === 0 ? (
        <p>You don't have access to any organizations yet.</p>
      ) : (
        <>
          <div className={s.orgList}>
            {userOrgs.map((org) => (
              <div key={org.id} className={s.orgItem}>
                <Checkbox
                  isSelected={formState.some((p) => p.id === org.id)}
                  onChange={(isChecked) =>
                    handleOrgToggle(org.id, org.category, isChecked)
                  }
                >
                  <div className={s.orgInfo}>
                    <span className={s.orgName}>{org.name}</span>
                    <span className={s.orgCategory}>{org.category}</span>
                  </div>
                </Checkbox>
              </div>
            ))}
          </div>

          <div className={s.actions}>
            <Button
              variant="primary"
              onPress={handleSubmit}
              isDisabled={!hasChanges || setPreferences.isPending}
            >
              {setPreferences.isPending ? "Saving..." : "Save Preferences"}
            </Button>
            <Button
              variant="secondary"
              onPress={handleReset}
              isDisabled={!hasChanges}
            >
              Reset
            </Button>
          </div>

          {setPreferences.error && (
            <div className={s.error}>
              Failed to save preferences. Please try again.
            </div>
          )}

          {setPreferences.isSuccess && (
            <div className={s.success}>Preferences saved successfully!</div>
          )}
        </>
      )}
    </div>
  );
}
